import { useRef } from "react";
import {
    motion,
    useMotionValue,
    useSpring,
    useTransform,
    useScroll,
} from "framer-motion";
import { ArrowUpRight, MessageCircle, ArrowDown } from "lucide-react";
import { EASE } from "./Reveal";
import { scrollToId, waLink, PHONE_DISPLAY, PHONE_TEL } from "../lib";

const LINES = [
    { text: "Precision", cls: "text-white" },
    { text: "for the", cls: "text-white" },
    { text: "operating", cls: "text-accent text-glow" },
    { text: "room.", cls: "text-white" },
];

const HERO_IMG =
    "https://images.pexels.com/photos/18112241/pexels-photo-18112241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHIPS = [
    { label: "Clean Room & Modular OT", cls: "left-2 sm:-left-6 top-8", delay: 1.1 },
    { label: "Shadowless LED Lighting", cls: "right-1 sm:-right-4 bottom-28", delay: 1.3 },
    { label: "CSSD Flow Design", cls: "left-6 sm:left-10 -bottom-5", delay: 1.5 },
];

export default function Hero() {
    const secRef = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const imgX = useTransform(sx, (v) => v * 16);
    const imgY = useTransform(sy, (v) => v * 16);
    const chipX = useTransform(sx, (v) => v * -26);
    const chipY = useTransform(sy, (v) => v * -20);
    const { scrollYProgress } = useScroll({
        target: secRef,
        offset: ["start start", "end start"],
    });
    const driftY = useTransform(scrollYProgress, [0, 1], [0, 80]);

    const onMove = (e) => {
        const r = secRef.current?.getBoundingClientRect();
        if (!r) return;
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={secRef}
            onMouseMove={onMove}
            className="relative flex min-h-[100svh] flex-col justify-center overflow-hidden pb-20 pt-28"
        >
            <div className="pointer-events-none absolute -top-44 right-[-12%] h-[560px] w-[560px] rounded-full bg-accent/10 blur-[150px]" />
            <div className="pointer-events-none absolute bottom-[-25%] left-[-12%] h-[500px] w-[500px] rounded-full bg-mint/5 blur-[150px]" />
            <div className="beam pointer-events-none absolute right-[8%] top-0 hidden h-[60%] w-[380px] lg:block" />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
                <div className="min-w-0 lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: EASE }}
                        className="mb-7 flex items-center gap-3"
                        data-testid="hero-badge"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
                        </span>
                        <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-300 sm:text-xs">
                            Since 1986 — Kolkata, West Bengal, India
                        </span>
                    </motion.div>

                    <h1 className="relative z-20 font-display text-[clamp(2rem,4.6vw,4.2rem)] font-extrabold uppercase leading-[1.04] tracking-tight">
                        {LINES.map((l, i) => (
                            <span key={l.text} className="mask-line">
                                <motion.span
                                    className={`block ${l.cls}`}
                                    initial={{ y: "112%" }}
                                    animate={{ y: "0%" }}
                                    transition={{
                                        duration: 0.95,
                                        delay: 0.15 + i * 0.13,
                                        ease: EASE,
                                    }}
                                >
                                    {l.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                        className="mt-7 max-w-xl text-base font-light leading-relaxed text-slate-400 sm:text-lg"
                    >
                        Manufacturers of hospital beds, OT tables and OT lights
                        — and designers of complete Clean Room &amp; Modular
                        Operation Theatres and CSSD — serving hospitals since
                        1986.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-enquire-button"
                            onClick={() => scrollToId("contact")}
                            className="group inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:scale-[1.04] hover:shadow-[0_0_40px_rgba(0,240,255,0.35)]"
                        >
                            Enquire Now
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                        </button>
                        <a
                            data-testid="hero-whatsapp-button"
                            href={waLink(
                                "Hello Science & Surgical, I'd like to enquire about your products.",
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-full border border-line px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:border-wa hover:text-wa"
                        >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp
                        </a>
                        <a
                            data-testid="hero-phone-button"
                            href={PHONE_TEL}
                            className="hidden font-mono text-xs tracking-[0.2em] text-slate-400 transition-colors hover:text-accent sm:inline-flex sm:items-center sm:gap-2"
                        >
                            {PHONE_DISPLAY}
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: driftY }}
                    className="relative h-[340px] sm:h-[420px] lg:col-span-5 lg:h-[540px]"
                    data-testid="hero-visual"
                >
                    <motion.div
                        style={{ x: imgX, y: imgY }}
                        className="absolute inset-0"
                    >
                        <motion.div
                            initial={{ clipPath: "inset(100% 0 0 0)" }}
                            animate={{ clipPath: "inset(0% 0 0 0)" }}
                            transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
                            className="relative h-full w-full overflow-hidden rounded-2xl border border-line"
                        >
                            <img
                                src={HERO_IMG}
                                alt="A modern operating theatre"
                                data-testid="hero-image"
                                className="spot-img h-full w-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-ink/25" />
                            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between border-t border-white/10 pt-4">
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">
                                    Clean Room &amp; Modular OT
                                </span>
                                <span className="font-mono text-[10px] tracking-[0.2em] text-accent">
                                    S&amp;S / 1986
                                </span>
                            </div>
                        </motion.div>
                    </motion.div>

                    {CHIPS.map((c) => (
                        <motion.div
                            key={c.label}
                            style={{ x: chipX, y: chipY }}
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: c.delay, duration: 0.6, ease: EASE }}
                            className={`absolute z-10 rounded-lg border border-line bg-panel/80 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-accent backdrop-blur-md ${c.cls}`}
                        >
                            {c.label}
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 0.8 }}
                className="pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 items-center gap-3 lg:flex"
            >
                <ArrowDown className="h-4 w-4 animate-bounce text-accent" />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Scroll
                </span>
            </motion.div>
        </section>
    );
}
